/*
 * Lenis + GSAP smooth scroll — Osmo setup
 * https://www.osmo.supply/resource/lenis-smooth-scroll-setup
 *
 * Loaded after gsap.min.js, ScrollTrigger.min.js and lenis.min.js (all `defer`, in order).
 * Drives Lenis off GSAP's ticker and keeps ScrollTrigger in sync with Lenis' scroll.
 *
 * Also the theme's one place for GSAP-wide setup, which runs even when smooth scroll itself is
 * skipped (reduced motion / theme editor): plugin registration and the single post-font refresh.
 */
(function () {
  if (!window.gsap) return;

  // Register every plugin that's loaded by now. SplitText / Draggable / InertiaPlugin are loaded
  // per-section in <body>, so they run after this file — their component scripts register them.
  const plugins = [window.ScrollTrigger, window.SplitText, window.Draggable, window.InertiaPlugin].filter(Boolean);
  if (plugins.length) gsap.registerPlugin(...plugins);

  // One refresh for every ScrollTrigger on the page once webfonts have landed (they shift layout).
  // Waits for DOMContentLoaded too, so the section components (deferred, later in the document)
  // have created their triggers. ScrollTrigger's own load/resize auto-refresh still applies.
  if (window.ScrollTrigger) {
    const domReady = new Promise((resolve) => {
      if (document.readyState !== 'loading') resolve();
      else document.addEventListener('DOMContentLoaded', resolve, { once: true });
    });
    const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    Promise.all([domReady, fontsReady]).then(() => ScrollTrigger.refresh());
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inThemeEditor = window.Shopify && window.Shopify.designMode;

  // Native scroll for reduce-motion users and inside the Shopify theme editor.
  if (reduceMotion || inThemeEditor || !window.Lenis || !window.ScrollTrigger) return;

  // allowNestedScroll lets scrollable drawers/menus (cart drawer, filters, search) scroll natively.
  const lenis = new Lenis({ allowNestedScroll: true });

  lenis.on('scroll', ScrollTrigger.update);

  gsap.ticker.add((time) => lenis.raf(time * 1000));

  gsap.ticker.lagSmoothing(0);

  // page-loader.js runs before this file and locks scroll (inline body overflow) while the intro
  // plays; Lenis didn't exist yet, so start stopped and let the loader's finish() call lenis.start().
  // Keyed off the lock itself, not just the gate class, so a loader that never ran can't strand Lenis.
  const loaderPlaying =
    document.documentElement.classList.contains('wp-loader-active') && document.body.style.overflow === 'hidden';
  if (loaderPlaying) lenis.stop();

  // Dawn locks scroll by adding `overflow-hidden` / `overflow-hidden-{breakpoint}` classes to <body>
  // (global.js, details-modal.js), which Lenis doesn't see. Mirror them — but only on transitions of
  // that class state, and never restart while the page loader or mobile menu (both lock via an inline
  // body overflow style) still want scroll held.
  const hasOverflowClass = () => [...document.body.classList].some((name) => name.startsWith('overflow-hidden'));
  let dawnLocked = hasOverflowClass();
  if (dawnLocked) lenis.stop();

  new MutationObserver(() => {
    const locked = hasOverflowClass();
    if (locked === dawnLocked) return;
    dawnLocked = locked;
    if (locked) lenis.stop();
    else if (document.body.style.overflow !== 'hidden') lenis.start();
  }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

  // Expose so other scripts can pause/resume scroll (e.g. lenis.stop() when a drawer/modal opens).
  window.lenis = lenis;
})();
